import React from 'react'
import { createRoot } from 'react-dom/client'
import Fuse from 'fuse.js'
import { ApolloProvider, useMutation, useQuery } from '@apollo/client'
import client from '../client'
import fetchOrganization from '../queries/find-organization'
import { formatDate } from '../utils'
import archiveUsers from '../mutations/archive-user'
import deleteUsers from '../mutations/delete-user'
import unarchiveUsers from '../mutations/unarchive-user'
import generatePassword from '../mutations/generate-password'
import Choices from 'choices.js'
import WarningModal from '../components/warning-modal'
import PubSub from 'pubsub-js'

const userRowClass = (user) => {
  if (user.active) {
    return ''
  } else {
    return 'disabled'
  }
}

function OrganizationUserList(props) {
  const [selectedUserIds, setSelectedUserIds] = React.useState([])
  const [archiveButtonText, setArchiveButtonText] = React.useState('Archive')
  const [deleteButtonText, setDeleteButtonText] = React.useState('Delete')
  const [unarchiveButtonText, setUnarchiveButtonText] =
    React.useState('Un-archive')
  const [activeTab, setActiveTab] = React.useState(
    props.activeTab || 'students'
  )
  const [searchText, setSearchText] = React.useState('')
  const [sort, setSort] = React.useState({
    field: 'insertedAt',
    direction: 'desc',
  })
  const [selectedYear, setSelectedYear] = React.useState('Graduation Year')
  const [selectBuilt, setSelectBuilt] = React.useState(false)
  const [resettingUserId, setResettingUserId] = React.useState(null)
  const [showWarning, setShowWarning] = React.useState(false)
  const [warningAction, setWarningAction] = React.useState(null)
  const [generatedPasswords, setGeneratedPasswords] = React.useState({})

  const { loading, error, data, refetch } = useQuery(fetchOrganization, {
    variables: { id: props.id },
  })
  const [archiveUsersMutation] = useMutation(archiveUsers)
  const [deleteUsersMutation] = useMutation(deleteUsers)
  const [unarchiveUsersMutation] = useMutation(unarchiveUsers)
  const [generatePasswordMutation] = useMutation(generatePassword)

  React.useEffect(() => {
    if (!selectBuilt && data && data.organization && data.organization.users) {
      const selects = document.querySelector('th select.choices')
      if (selects) {
        // Standard hates this
        const opts = {
          shouldSort: false,
          shouldSortItems: false,
          itemSelectText: '',
          searchPlaceholderValue: 'Search year',
        }
        const choices = new Choices('th select.choices', opts) // eslint-disable-line
      }

      setSelectBuilt(true)
    }
  }, [data])

  if (loading) return <div>Loading...</div>
  if (error) console.log(error) //return <div>Error :(</div>

  const setResetPasswordWarning = (user) => {
    setShowWarning(true)
    setWarningAction(() => handleGeneratePassword(user))
  }

  const dismissWarning = (e) => {
    e.preventDefault()

    setShowWarning(false)
    setWarningAction(null)
  }

  const signInAs = (user) => {
    document.location.href = `/admin/login-as?user_id=${user.id}`
  }

  const handleGeneratePassword = (user) => {
    generatedPasswords[user.id] = null
    setResettingUserId(user.id)
    setGeneratedPasswords(generatedPasswords)
    setShowWarning(false)

    generatePasswordMutation({
      variables: { userId: user.id },
    }).then((operation) => {
      generatedPasswords[user.id] = operation.data.generatePassword

      window.setTimeout(() => {
        setResettingUserId(null)
        setGeneratedPasswords(generatedPasswords)
      }, 500)
    })
  }

  const handleArchive = () => {
    const { id } = props

    if (selectedUserIds.length === 0) {
      return
    }

    setArchiveButtonText('Archiving')
    archiveUsersMutation({
      variables: { userIds: selectedUserIds, organizationId: id },
    }).then(() => {
      window.setTimeout(() => {
        setArchiveButtonText('Archive')
        setSelectedUserIds([])
        refetch()
      }, 2000)
    })
  }

  const handleDelete = () => {
    if (selectedUserIds.length === 0) {
      return
    }
    if (
      data.organization.users.filter((user) => {
        return (
          selectedUserIds.includes(user.id.toString()) &&
          user.lastQuestionAnsweredByStudent
        )
      }).length > 0
    ) {
      alert(`A user has an answered question, unable to delete!`)
      return
    }

    setDeleteButtonText('Deleting')
    deleteUsersMutation({
      variables: { userIds: selectedUserIds, organizationId: id },
    }).then(() => {
      window.setTimeout(() => {
        setDeleteButtonText('Delete')
        setSelectedUserIds([])
        refetch()
      }, 2000)
    })
  }

  const handleUnArchive = () => {
    const { id } = this.props
    const { selectedUserIds } = this.state

    if (selectedUserIds.length === 0) {
      return
    }

    setUnarchiveButtonText('Un-archiving')
    unarchiveUsersMutation({
      variables: { userIds: selectedUserIds, organizationId: id },
    }).then(() => {
      window.setTimeout(() => {
        setUnarchiveButtonText('Un-archive')
        setSelectedUserIds([])
        refetch()
      }, 2000)
    })
  }

  const setSearchField = (newField) => {
    const { activeTab } = this.state
    if (activeTab === 'students' && newField === 'updatedAt') {
      newField = 'lastQuestionAnsweredByStudent'
    } else if (newField === 'updatedAt') {
      newField = 'teacherDateOfLastActivity'
    }
    let newDirection

    if (newField === sort.field) {
      if (sort.direction === 'asc') {
        newDirection = 'desc'
      } else {
        newDirection = 'asc'
      }
    } else {
      newDirection = 'desc'
    }

    setSort({ field: newField, direction: newDirection })
  }

  const toggleSelectAll = () => {
    const allUserIds = filterUsersToDisplay().map((user) => {
      return user.id
    })

    if (allUserIds.length === selectedUserIds.length) {
      setSelectedUserIds([])
    } else {
      setSelectedUserIds(allUserIds)
    }
  }

  const toggleUserChecked = (id) => {
    const idIndex = selectedUserIds.indexOf(id)

    if (idIndex > -1) {
      selectedUserIds.splice(idIndex, 1)
      setSelectedUserIds(selectedUserIds)
    } else {
      setSelectedUserIds([...new Set([...selectedUserIds, ...[id]])])
    }
  }

  const editUser = (userId) => {
    window.location.href = `/admin/organizations/${props.id}/users/${userId}/edit`
  }

  const showTab = (tab) => {
    setActiveTab(tab)
    setSelectBuilt(false)
  }

  const handleSearch = (e) => {
    setSearchText(e.target.value)
  }

  const handleGraduationYearChange = (e) => {
    let year = e.target.value

    if (year === null) return

    if (year && (year === 'All' || year === 'Graduation Year')) {
      year = 'All'
    } else {
      year = parseInt(year)
    }

    setSelectedYear(year)
  }

  const sortByField = (users) => {
    return users.sort((userA, userB) => {
      if (sort.direction === 'desc') {
        if (userB[sort.field] === null) return -1
        if (userA[sort.field] > userB[sort.field]) return -1
        if (userA[sort.field] === userB[sort.field]) return 0
        return 1
      } else {
        if (userA[sort.field] === null) return -1
        if (userB[sort.field] > userA[sort.field]) return -1
        if (userB[sort.field] === userA[sort.field]) return 0
        return 1
      }
    })
  }

  const sortByName = (users) => {
    return users.sort((userA, userB) => {
      const userALastName = userA.name.split(' ')[1]
      const userBLastName = userB.name.split(' ')[1]
      if (userALastName === null) return -1
      if (userBLastName > userALastName) return -1
      if (userBLastName === userALastName) return 0
      return 1
    })
  }

  const sortBySearch = (users) => {
    if (searchText === null || searchText === '') {
      return users
    }

    const fuseOptions = {
      shouldSort: true,
      tokenize: true,
      threshold: 1,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: ['name', 'email'],
    }

    const fuse = new Fuse(users, fuseOptions)

    return fuse.search(searchText)
  }

  const showArchivedLast = (users) => {
    return users.sort((a, b) => {
      if (a.active === b.active) {
        return 0
      }

      if (a.active) {
        return -1
      }

      return 1
    })
  }

  const showAdminFirst = (users) => {
    return users.sort((a, b) => {
      if (a.role === b.role) {
        return 0
      }

      if (a.role === 'School Admin') {
        return -1
      }

      return 1
    })
  }

  const filterUsersToDisplay = () => {
    if (data && data.organization && data.organization.users) {
      return data.organization.users.filter((u) => {
        if (activeTab === 'students') {
          if (
            selectedYear !== 'Graduation Year' &&
            selectedYear !== 'All' &&
            u.graduationYear !== selectedYear
          ) {
            return false
          }

          return u.role === 'Student'
        } else {
          return u.role !== 'Student'
        }
      })
    } else {
      return []
    }
  }

  const renderOption = (option, selected) => {
    if (option === selected) {
      return (
        <option key={option} value={option} selected>
          {option}
        </option>
      )
    } else {
      return (
        <option key={option} value={option}>
          {option}
        </option>
      )
    }
  }

  const assignmentCountByRole = (user) => {
    if (user.role === 'Student') {
      return user.studentAssignmentCount
    } else {
      return user.teacherAssignmentCount
    }
  }

  const lastModified = (user) => {
    if (user.role === 'Student') {
      return (
        <td>
          <div className="match-height">
            {formatDate(user.lastQuestionAnsweredByStudent, 'MM/dd/yy')}
          </div>
        </td>
      )
    } else {
      return (
        <td>
          <div className="match-height" title={user.lastActiveProjectByTeacher}>
            {formatDate(user.teacherDateOfLastActivity, 'MM/dd/yy')}
          </div>
        </td>
      )
    }
  }

  const teachersLinkClass = activeTab === 'teachers' ? 'active' : ''
  const studentsLinkClass = activeTab === 'students' ? 'active' : ''
  const disableArchive = selectedUserIds.length === 0
  let years = []

  if (data && data.organization && data.organization.users) {
    years = Array.from(
      new Set(data.organization.users.map((u) => u.graduationYear))
    ).sort()
  }

  const displayedUsers =
    activeTab === 'teachers'
      ? sortBySearch(
          showAdminFirst(showArchivedLast(sortByField(filterUsersToDisplay())))
        )
      : sortBySearch(showArchivedLast(sortByName(filterUsersToDisplay())))

  const refreshSubscriber = () => {
    refetch()
  }

  PubSub.subscribe('refresh_users', refreshSubscriber)

  return (
    <form>
      <div className="container">
        <div className="header">
          <h2>
            Users{' '}
            {data && data.organization && (
              <span>
                <br />({data.organization.name})
              </span>
            )}
          </h2>
          <nav className="actions">
            {activeTab === 'students' && props.role === 'Admin' && false && (
              <a
                className={disableArchive ? 'disabled button' : 'delete-button'}
                onClick={handleDelete}
              >
                {deleteButtonText}
              </a>
            )}
            <a
              className={disableArchive ? 'disabled button' : 'button'}
              onClick={handleArchive}
            >
              {archiveButtonText}
            </a>
            <a
              className={disableArchive ? 'disabled button' : 'button'}
              onClick={handleUnArchive}
            >
              {unarchiveButtonText}
            </a>
            <a
              className="button"
              href={`/admin/organizations/${props.id}/users/new`}
            >
              Add User
            </a>
            <a
              className="button"
              href={`/admin/organizations/${props.id}/users/import-${activeTab}`}
            >
              Batch Import (CSV)
            </a>
          </nav>
        </div>
      </div>
      <div className="container">
        <div className="header">
          <nav
            className="subnav"
            role="navigation"
            aria-label="Organization Navigation"
          >
            <a
              className={studentsLinkClass}
              onClick={() => showTab('students')}
            >
              Students
            </a>
            <a
              className={teachersLinkClass}
              onClick={() => showTab('teachers')}
            >
              Teachers
            </a>
            <div className="search">
              <i className="material-icons">search</i>
              <input
                type="text"
                value={searchText}
                onChange={handleSearch}
                placeholder="Search Users..."
              />
            </div>
          </nav>
        </div>
        <table className="fancy remove-top-margin">
          <thead>
            <tr>
              <th onClick={toggleSelectAll} style={{ cursor: 'pointer' }}>
                <div>Select All</div>
              </th>
              <th>
                <div>Name</div>
              </th>
              <th>
                <div>Email</div>
              </th>
              {activeTab === 'students' && (
                <th>
                  <div>
                    <select
                      className="choices"
                      onChange={handleGraduationYearChange}
                    >
                      <option>Graduation Year</option>
                      {['All', ...years].map((y) =>
                        renderOption(y, selectedYear)
                      )}
                    </select>
                  </div>
                </th>
              )}
              <th>
                <div>Role</div>
              </th>
              <th>
                <div>Questions</div>
              </th>
              {activeTab === 'teachers' && (
                <th>
                  <div>Projects</div>
                </th>
              )}
              <th
                onClick={() => setSearchField('updatedAt')}
                style={{ cursor: 'pointer' }}
              >
                <div>Last Active</div>
              </th>
              {(props.role === 'Admin' || props.role === 'School Admin') && (
                <th>
                  <div>Reset Password</div>
                </th>
              )}
              {props.role === 'Admin' && (
                <th>
                  <div>Login</div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {displayedUsers &&
              displayedUsers.map((user) => (
                <tr key={user.id} className={userRowClass(user)}>
                  <td>
                    <div
                      className="match-height"
                      onClick={() => toggleUserChecked(user.id)}
                    >
                      <input
                        className="bulk-action-checkbox"
                        type="checkbox"
                        value={user.id}
                        readOnly
                        checked={
                          selectedUserIds.indexOf(user.id) > -1 ? 'checked' : ''
                        }
                      />
                      <label htmlFor="user_id_#{u.id}" />
                    </div>
                  </td>
                  <td>
                    <div className="match-height">
                      {['Admin', 'School Admin'].includes(props.role) && (
                        <a onClick={() => editUser(user.id)}>{user.name}</a>
                      )}
                      {!['Admin', 'School Admin'].includes(props.role) &&
                        user.name}
                    </div>
                  </td>
                  <td>
                    <div className="match-height">{user.email}</div>
                  </td>
                  {activeTab === 'students' && (
                    <td>
                      <div className="match-height">
                        {user.graduationYear || 'N/A'}
                      </div>
                    </td>
                  )}
                  <td>
                    <div className="match-height">{user.role}</div>
                  </td>
                  <td>
                    <div
                      className="match-height"
                      style={{ alignItems: 'center' }}
                    >
                      {' '}
                      {assignmentCountByRole(user)}{' '}
                    </div>
                  </td>
                  {user.role !== 'Student' && (
                    <td>
                      <div
                        className="match-height"
                        style={{ alignItems: 'center' }}
                      >
                        {' '}
                        {user.allActiveProjectsByTeacherCount}{' '}
                      </div>
                    </td>
                  )}
                  {lastModified(user)}
                  {(props.role === 'Admin' ||
                    props.role === 'School Admin') && (
                    <td>
                      <div
                        className="match-height"
                        style={{ alignItems: 'center' }}
                      >
                        <a onClick={() => setResetPasswordWarning(user)}>
                          <img
                            src="/images/icon-lock.png"
                            width="20"
                            height="20"
                          />
                        </a>
                        {resettingUserId === user.id && <span>Resetting</span>}
                        {generatedPasswords[user.id] && (
                          <span>
                            New Password:
                            <br />
                            {generatedPasswords[user.id]}
                          </span>
                        )}
                      </div>
                    </td>
                  )}
                  {props.role === 'Admin' && (
                    <td>
                      <div className="match-height">
                        <a onClick={() => signInAs(user)}>Sign in as</a>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
        <WarningModal
          isOpen={showWarning}
          onClose={dismissWarning}
          onConfirm={warningAction}
          confirmText="Continue"
          dismissText="Cancel"
        >
          <h2 style={{ marginTop: 48, marginBottom: 54 }}>
            Generate a new password for a user who is locked out. (You'll need
            to provide it to the user.)
          </h2>
        </WarningModal>
      </div>
    </form>
  )
}

const organizationUserList = document.getElementById('organization-users')
if (organizationUserList) {
  const id = organizationUserList.getAttribute('data-id')
  const role = organizationUserList.getAttribute('data-role')
  const activeTab = organizationUserList.getAttribute('data-active-tab')
  const props = { id, role, activeTab }

  const root = createRoot(organizationUserList)
  root.render(
    <ApolloProvider client={client}>
      <OrganizationUserList {...props} />
    </ApolloProvider>
  )
}
